import type { DaySchedule, ScheduleException, WorkingHours } from '@/api/types';

export const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;

export interface NormalizedHours {
    regular: Record<string, DaySchedule>;
    exceptions: ScheduleException[];
}

export const DEFAULT_DAY: DaySchedule = { from: '09:00', to: '21:00' };

/**
 * locations.working_hours исторически бывает трёх видов: {regular, exceptions},
 * {is_24h: true} и {days: {"1": "09:00-21:00"}}. Редактор работает только с первым,
 * сохраняем тоже его — старые форматы конвертируются при первом сохранении.
 */
export function normalizeHours(wh: WorkingHours | null | undefined): NormalizedHours {
    const regular: Record<string, DaySchedule> = {};
    if (wh?.regular) {
        for (let d = 1; d <= 7; d++) regular[d] = wh.regular[d] ?? { closed: true };
        return { regular, exceptions: wh.exceptions ?? [] };
    }
    for (let d = 1; d <= 7; d++) {
        if (!wh) regular[d] = { ...DEFAULT_DAY };
        else if (wh.is_24h) regular[d] = { all_day: true };
        else if (wh.days) {
            const range = wh.days[d];
            if (range) {
                const [from, to] = range.split('-');
                regular[d] = { from, to };
            } else regular[d] = { closed: true };
        } else regular[d] = { ...DEFAULT_DAY };
    }
    return { regular, exceptions: [] };
}

/** Убирает лишние поля: у закрытого/круглосуточного дня нет from/to. */
export function cleanDay<T extends DaySchedule>(day: T): T {
    const { from, to, closed, all_day, ...rest } = day;
    if (closed) return { ...rest, closed: true, all_day: false } as T;
    if (all_day) return { ...rest, closed: false, all_day: true } as T;
    return { ...rest, closed: false, all_day: false, from: from || DEFAULT_DAY.from, to: to || DEFAULT_DAY.to } as T;
}

export function serializeHours(h: NormalizedHours): WorkingHours {
    const regular: Record<string, DaySchedule> = {};
    for (let d = 1; d <= 7; d++) {
        const day = cleanDay(h.regular[d] ?? { closed: true });
        // В regular флаги-«false» не пишем — формат как у старой админки.
        regular[d] = day.closed ? { closed: true } : day.all_day ? { all_day: true } : { from: day.from, to: day.to };
    }
    const exceptions = h.exceptions.filter((e) => e.date).map((e) => cleanDay(e));
    return { regular, exceptions };
}

export function describeDay(day: DaySchedule | undefined): string {
    if (!day || day.closed) return 'выходной';
    if (day.all_day) return 'круглосуточно';
    return `${day.from}–${day.to}`;
}

/** Короткая сводка «Пн–Пт 09:00–21:00, Сб–Вс выходной» для списка. */
export function summarizeHours(wh: WorkingHours | null | undefined): string {
    if (!wh) return '—';
    const { regular } = normalizeHours(wh);
    const groups: { start: number; end: number; text: string }[] = [];
    for (let d = 1; d <= 7; d++) {
        const text = describeDay(regular[d]);
        const last = groups[groups.length - 1];
        if (last && last.text === text) last.end = d;
        else groups.push({ start: d, end: d, text });
    }
    if (groups.length === 1) return `ежедневно ${groups[0].text}`;
    return groups
        .map((g) => `${DAYS[g.start - 1]}${g.end > g.start ? '–' + DAYS[g.end - 1] : ''} ${g.text}`)
        .join(', ');
}
